/**
 * Géométrie du profil d'altitude interactif.
 *
 * Tout ce qui décide — facteur d'échelle, déplacement, fenêtre visible, pas
 * des graduations, rangée d'un libellé de marqueur — vit ici, sans canevas ni
 * DOM. Le tracé, lui, est dans `elevationChart.ts` : c'est ce partage qui rend
 * le zoom éprouvable par des tests, là où le rendu ne l'est pas.
 *
 * Portage de `ElevationProfileView.kt`. Les constantes viennent de là, y
 * compris le pas de graduation et l'écart minimal entre deux libellés.
 */

import { lowerBound } from '../core/geo'

/** Le profil entier tient dans la largeur : en deçà, il n'y a rien à voir. */
export const MIN_SCALE = 1

/**
 * Au-delà de ×20, deux points voisins d'un relevé GPS s'écartent de toute la
 * largeur : on zoome sur du vide interpolé, pas sur du terrain.
 */
export const MAX_SCALE = 20

/** Écart minimal entre deux libellés d'une même rangée, en pixels. */
const LABEL_GAP_PX = 6

/**
 * Pas de graduation possibles, en kilomètres.
 *
 * Une liste fermée plutôt qu'un calcul : elle garantit des valeurs qu'on lit
 * d'un coup d'œil — 0,5 km, 2 km, 50 km — là où un pas calculé donnerait
 * 3,7 km.
 */
const NICE_STEPS: readonly number[] = [0.1, 0.2, 0.5, 1, 2, 5, 10, 20, 50, 100]

/**
 * Pas de graduation le plus proche du quart de la plage affichée.
 *
 * Quatre intervalles visés : moins serait pauvre, plus se chevaucherait aux
 * petites largeurs. À égalité, le plus petit l'emporte — c'est ce que fait la
 * comparaison stricte d'Android, et deux pas équidistants n'ont aucune raison
 * de basculer d'une version à l'autre.
 */
export function niceStep(rangeKm: number): number {
  const target = rangeKm / 4
  let best = NICE_STEPS[0]!
  let bestDiff = Math.abs(best - target)
  for (let i = 1; i < NICE_STEPS.length; i++) {
    const diff = Math.abs(NICE_STEPS[i]! - target)
    if (diff < bestDiff) {
      best = NICE_STEPS[i]!
      bestDiff = diff
    }
  }
  return best
}

/**
 * Borne le facteur d'échelle à la plage utile.
 *
 * Seul `NaN` justifie un repli : il vient d'un pincement dont les deux doigts
 * se confondent, et il traverserait les comparaisons sans être borné, laissant
 * un canevas vide. Un infini, lui, se borne comme n'importe quelle valeur.
 */
export function clampScale(scale: number): number {
  if (Number.isNaN(scale)) return MIN_SCALE
  return Math.min(Math.max(scale, MIN_SCALE), MAX_SCALE)
}

/**
 * Ramène `-0` à `0`.
 *
 * Les deux valent la même chose en calcul, mais pas pour `Object.is` : un zéro
 * négatif traverse les mesures et fait diverger des comparaisons qui devraient
 * tenir. Autant n'en produire qu'une forme.
 */
function zeroSafe(value: number): number {
  return value === 0 ? 0 : value
}

/**
 * Borne le déplacement pour que le contenu couvre toujours la vue.
 *
 * À l'échelle ×1 les deux bornes se confondent en zéro : c'est de là que vient
 * le blocage du défilement quand on n'a pas zoomé, et non d'un test explicite.
 */
export function clampPan(
  panOffset: number,
  scale: number,
  viewWidth: number,
): number {
  const contentWidth = viewWidth * scale
  const min = -(contentWidth - viewWidth)
  if (!Number.isFinite(panOffset)) return 0
  return zeroSafe(Math.min(Math.max(panOffset, min), 0))
}

/**
 * Déplacement conservant sous le doigt le point qu'on pince.
 *
 * Sans cette correction, le zoom part du bord gauche et le relief visé fuit
 * l'écran au premier pincement.
 *
 * @param focusX Abscisse du foyer **relative à la zone de tracé**, marge ôtée.
 */
export function panForZoom(
  focusX: number,
  panOffset: number,
  oldScale: number,
  newScale: number,
): number {
  if (oldScale <= 0) return panOffset
  return focusX - (focusX - panOffset) * (newScale / oldScale)
}

export interface DistanceRange {
  readonly start: number
  readonly end: number
}

/** Plage de distances effectivement affichée, en mètres. */
export function visibleDistanceRange(
  panOffset: number,
  scale: number,
  viewWidth: number,
  maxDistance: number,
): DistanceRange {
  const contentWidth = viewWidth * scale
  if (contentWidth <= 0) return { start: 0, end: maxDistance }
  return {
    start: zeroSafe((-panOffset / contentWidth) * maxDistance),
    end: zeroSafe(((-panOffset + viewWidth) / contentWidth) * maxDistance),
  }
}

/**
 * Altitude du profil à une distance curviligne, par interpolation linéaire.
 *
 * Un marqueur tombe presque toujours entre deux relevés : le poser à
 * l'altitude du point le plus proche le décollerait visiblement de la courbe
 * dans les pentes raides.
 */
export function elevationAt(
  distances: readonly number[],
  elevations: readonly number[],
  distance: number,
): number {
  if (elevations.length === 0) return 0

  const index = lowerBound(distances, distance)
  if (index <= 0) return elevations[0]!
  if (index >= elevations.length) return elevations[elevations.length - 1]!

  const d0 = distances[index - 1]!
  const d1 = distances[index]!
  const e0 = elevations[index - 1]!
  const e1 = elevations[index]!
  const t = d1 > d0 ? (distance - d0) / (d1 - d0) : 0
  return e0 + (e1 - e0) * t
}

export interface MarkerLabel {
  /** Abscisse écran du marqueur, en pixels CSS. */
  readonly x: number
  /** Largeur du libellé, mesurée par le contexte de dessin. */
  readonly width: number
}

/** Rangée attribuée à un libellé, ou `null` quand les deux sont prises. */
export type MarkerRow = 'lower' | 'upper' | null

/**
 * Répartit les libellés sur deux rangées sans qu'ils se chevauchent.
 *
 * Les marqueurs arrivent triés par distance, donc par abscisse croissante : il
 * suffit de retenir où finit le dernier libellé de chaque rangée. La rangée
 * basse est essayée d'abord — plus proche de la courbe, donc plus lisible —,
 * la haute sert de dégagement, et au-delà le libellé est abandonné. Le point
 * et le trait, eux, restent toujours dessinés : c'est ce qui permet de zoomer
 * pour révéler ce que la place ne permettait pas d'écrire.
 *
 * Les marqueurs hors de la zone de tracé doivent être écartés **avant** cet
 * appel : sinon ils consommeraient une rangée qu'aucun libellé visible
 * n'occupe.
 */
export function layoutMarkerRows(
  labels: readonly MarkerLabel[],
): MarkerRow[] {
  let lowerEnd = Number.NEGATIVE_INFINITY
  let upperEnd = Number.NEGATIVE_INFINITY

  return labels.map(({ x, width }) => {
    const start = x - width / 2
    if (start > lowerEnd + LABEL_GAP_PX) {
      lowerEnd = start + width
      return 'lower'
    }
    if (start > upperEnd + LABEL_GAP_PX) {
      upperEnd = start + width
      return 'upper'
    }
    return null
  })
}

/**
 * Marqueurs à poser sur la courbe, dans l'ordre des distances croissantes.
 *
 * Le symbole est tronqué à quatre caractères : l'interface en borne deux — un
 * émoji en compte deux — mais un GPX importé peut en porter des milliers, qu'il
 * serait absurde de mesurer puis de dessiner.
 */
export interface ProfileMarker {
  readonly distance: number
  readonly elevation: number
  readonly symbol: string
}

export function profileMarkers(
  markers: readonly { distanceKm: number; symbol: string; visible: boolean }[],
  toRawDistance: (km: number) => number,
  distances: readonly number[],
  elevations: readonly number[],
): ProfileMarker[] {
  if (distances.length === 0) return []
  const maxDistance = distances[distances.length - 1]!

  return markers
    .filter((marker) => marker.visible)
    .map((marker) => ({
      distance: toRawDistance(marker.distanceKm),
      symbol: marker.symbol.slice(0, 4),
    }))
    // Un marqueur posé avant le départ réel a une distance négative, et un
    // marqueur d'une trace raccourcie dépasse la fin : ni l'un ni l'autre n'a
    // de place sur la courbe.
    .filter((marker) => marker.distance >= 0 && marker.distance <= maxDistance)
    .sort((a, b) => a.distance - b.distance)
    .map((marker) => ({
      ...marker,
      elevation: elevationAt(distances, elevations, marker.distance),
    }))
}
